import { api } from './api';

// ============================================================
// Types
// ============================================================

export interface HubCampaignCard {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  status: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  attribute_count: number;
}

export interface HubAttribute {
  id: number;
  name: string;
  description: string | null;
  attribute_type: 'numeric' | 'boolean' | 'enum' | 'scale';
  config: Record<string, any>;
  comment_mode: 'disabled' | 'optional' | 'required';
  points_reward: number;
  min_submissions: number;
  submission_count: number;
  data_mode: 'aggregated' | 'factual';
  display_order: number;
}

export interface HubCampaignDetail {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  status: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  attributes: HubAttribute[];
  created_at: string;
}

export interface HubSubmission {
  id: number;
  attribute_id: number;
  game_id: number;
  username: string;
  value: { v: any };
  comment: string | null;
  is_edit: boolean;
  status: number;
  points_awarded: number;
  created_at: string;
}

export interface HubAggregatedValue {
  id: number;
  attribute_id: number;
  game_id: number;
  attribute_name: string;
  attribute_type: string;
  attribute_config: Record<string, any>;
  submission_count: number;
  aggregated_value: Record<string, any>;
  is_published: boolean;
  last_calculated_at: string;
}

export interface HubPointsHistory {
  id: number;
  amount: number;
  source_type: string;
  source_id: number | null;
  source_detail: {
    attribute_name: string;
    is_edit: boolean;
  } | null;
  description: string | null;
  balance_after: number;
  created_at: string;
}

export interface HubLeaderboardEntry {
  rank: number;
  username: string;
  display_name: string;
  avatar: string | null;
  contribution_points: number;
  submission_count: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Admin types
export interface AdminHubCampaign {
  id: number;
  title: string;
  description: string | null;
  image_url: string | null;
  status: number;
  is_permanent: boolean;
  start_date: string | null;
  end_date: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface AdminHubAttribute {
  id: number;
  campaign: number;
  name: string;
  description: string | null;
  attribute_type: string;
  config: Record<string, any>;
  aggregation_method: string;
  comment_mode: string;
  approval_mode: string;
  data_mode: string;
  min_submissions: number;
  points_reward: number;
  status: number;
  display_order: number;
  target_content_type: string;
  created_at: string;
  updated_at: string;
}

export interface AdminHubSubmission {
  id: number;
  attribute_id: number;
  attribute_name: string;
  attribute_type: string;
  attribute_config: Record<string, any>;
  data_mode: string;
  user_id: number;
  username: string;
  game_id: number;
  game_name: string | null;
  value: { v: any };
  comment: string | null;
  is_edit: boolean;
  status: number;
  reviewed_by: number | null;
  reviewed_at: string | null;
  points_awarded: number;
  created_at: string;
  previous_value: any | null;
}

export interface AdminHubStats {
  campaigns_active: number;
  attributes_active: number;
  submissions_total: number;
  submissions_pending: number;
  submissions_approved: number;
}

export interface HubOptions {
  announcement: {
    enabled: boolean;
    text: string;
    severity: 'info' | 'warning' | 'error' | 'success';
  };
  lock: {
    locked: boolean;
    message: string;
  };
}

// ============================================================
// Public API
// ============================================================

export const hubGetOptions = async (): Promise<HubOptions> => {
  const response = await api.get('/hub/options/');
  return response.data;
};

export const hubGetCampaigns = async (): Promise<HubCampaignCard[]> => {
  const response = await api.get('/hub/campaigns/');
  return response.data;
};

export const hubGetCampaignDetail = async (id: number): Promise<HubCampaignDetail> => {
  const response = await api.get(`/hub/campaigns/${id}/`);
  return response.data;
};

export const hubGetGameAttributes = async (gameId: number): Promise<HubAggregatedValue[]> => {
  const response = await api.get(`/hub/games/${gameId}/attributes/`);
  return response.data;
};

export const hubSubmitData = async (data: {
  attribute_id: number;
  game_id: number;
  value: { v: any };
  comment?: string;
}): Promise<HubSubmission> => {
  const response = await api.post('/hub/submissions/', data);
  return response.data;
};

export const hubGetMySubmissions = async (params?: {
  game_id?: number;
  attribute_id?: number;
  page?: number;
}): Promise<PaginatedResponse<HubSubmission>> => {
  const urlParams = new URLSearchParams();
  if (params?.game_id) urlParams.append('game_id', params.game_id.toString());
  if (params?.attribute_id) urlParams.append('attribute_id', params.attribute_id.toString());
  if (params?.page) urlParams.append('page', params.page.toString());
  const response = await api.get(`/hub/submissions/mine/?${urlParams.toString()}`);
  return response.data;
};

export const hubGetMyGameSubmissions = async (gameId: number): Promise<{
  submissions: HubSubmission[];
  pending_attribute_ids: number[];
}> => {
  const response = await api.get(`/hub/games/${gameId}/my-submissions/`);
  return response.data;
};

export const hubGetMyGameConfirmations = async (gameId: number): Promise<number[]> => {
  const response = await api.get(`/hub/games/${gameId}/my-confirmations/`);
  return response.data;
};

export const hubConfirmValue = async (data: {
  attribute_id: number;
  game_id: number;
}): Promise<{ status: string }> => {
  const response = await api.post('/hub/confirmations/', data);
  return response.data;
};

export const hubGetPointsHistory = async (page?: number): Promise<PaginatedResponse<HubPointsHistory>> => {
  const urlParams = new URLSearchParams();
  if (page) urlParams.append('page', page.toString());
  const response = await api.get(`/hub/points/history/?${urlParams.toString()}`);
  return response.data;
};

export const hubGetLeaderboard = async (): Promise<HubLeaderboardEntry[]> => {
  const response = await api.get('/hub/leaderboard/');
  return response.data;
};

// ============================================================
// Admin API
// ============================================================

export const adminHubGetCampaigns = async (): Promise<AdminHubCampaign[]> => {
  const response = await api.get('/admin/hub/campaigns/');
  return response.data;
};

export const adminHubCreateCampaign = async (data: Partial<AdminHubCampaign>): Promise<AdminHubCampaign> => {
  const response = await api.post('/admin/hub/campaigns/', data);
  return response.data;
};

export const adminHubUpdateCampaign = async (id: number, data: Partial<AdminHubCampaign>): Promise<AdminHubCampaign> => {
  const response = await api.patch(`/admin/hub/campaigns/${id}/`, data);
  return response.data;
};

export const adminHubDeleteCampaign = async (id: number): Promise<void> => {
  await api.delete(`/admin/hub/campaigns/${id}/`);
};

export const adminHubGetAttributes = async (campaignId?: number): Promise<AdminHubAttribute[]> => {
  const urlParams = new URLSearchParams();
  if (campaignId) urlParams.append('campaign_id', campaignId.toString());
  const response = await api.get(`/admin/hub/attributes/?${urlParams.toString()}`);
  return response.data;
};

export const adminHubCreateAttribute = async (data: Partial<AdminHubAttribute>): Promise<AdminHubAttribute> => {
  const response = await api.post('/admin/hub/attributes/', data);
  return response.data;
};

export const adminHubUpdateAttribute = async (id: number, data: Partial<AdminHubAttribute>): Promise<AdminHubAttribute> => {
  const response = await api.patch(`/admin/hub/attributes/${id}/`, data);
  return response.data;
};

export const adminHubDeleteAttribute = async (id: number): Promise<void> => {
  await api.delete(`/admin/hub/attributes/${id}/`);
};

export const adminHubGetPendingSubmissions = async (params?: {
  campaign_id?: number;
  attribute_id?: number;
}): Promise<AdminHubSubmission[]> => {
  const urlParams = new URLSearchParams();
  if (params?.campaign_id) urlParams.append('campaign_id', params.campaign_id.toString());
  if (params?.attribute_id) urlParams.append('attribute_id', params.attribute_id.toString());
  const response = await api.get(`/admin/hub/submissions/pending/?${urlParams.toString()}`);
  return response.data;
};

export const adminHubApproveSubmission = async (id: number): Promise<AdminHubSubmission> => {
  const response = await api.post(`/admin/hub/submissions/${id}/approve/`);
  return response.data;
};

export const adminHubRejectSubmission = async (id: number): Promise<AdminHubSubmission> => {
  const response = await api.post(`/admin/hub/submissions/${id}/reject/`);
  return response.data;
};

export const adminHubBatchApprove = async (ids: number[]): Promise<{ approved: number[]; errors: any[] }> => {
  const response = await api.post('/admin/hub/submissions/batch-approve/', { ids });
  return response.data;
};

export const adminHubBatchReject = async (ids: number[]): Promise<{ rejected: number[]; errors: any[] }> => {
  const response = await api.post('/admin/hub/submissions/batch-reject/', { ids });
  return response.data;
};

export const adminHubGetStats = async (): Promise<AdminHubStats> => {
  const response = await api.get('/admin/hub/stats/');
  return response.data;
};

export const adminHubGetSettings = async (): Promise<HubOptions> => {
  const response = await api.get('/admin/hub/settings/');
  return response.data;
};

export const adminHubUpdateSettings = async (data: Partial<HubOptions>): Promise<Partial<HubOptions>> => {
  const response = await api.patch('/admin/hub/settings/', data);
  return response.data;
};
